import { ComponentPropsWithoutRef, ComponentPropsWithRef, Fragment, VFC } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import trackUserEvent from '@/util/eventTracking';
import Image from 'next/image';
import Link from 'next/link';
import cn from 'classnames';

import { LinkedInButton, TwitterButton, TelegramButton, DiscordButton, MailButton } from 'ui';

const SocialMedia = [
  {
    name: 'LinkedIn',
    href: {
      'en-US': 'https://www.linkedin.com/company/structurefi/',
      'es-SV': 'https://www.linkedin.com/company/structurefi/',
    },
    Icon: LinkedInButton,
  },
  {
    name: 'Twitter',
    href: {
      'en-US': 'https://twitter.com/structure_fi',
      'es-SV': 'https://twitter.com/StructureFi_Esp',
    },
    Icon: TwitterButton,
  },
  {
    name: 'Telegram',
    href: {
      'en-US': 'https://t.me/Structure_Official',
      'es-SV': 'https://t.me/Structure_Official',
    },
    Icon: TelegramButton,
  },
  {
    name: 'Discord',
    href: {
      'en-US': 'https://discord.gg/5KGUYEsxBv',
      'es-SV': 'https://discord.gg/5KGUYEsxBv',
    },
    Icon: DiscordButton,
  },
  {
    name: 'Email',
    href: {
      'en-US': 'mailto:support@structure.fi',
      'es-SV': 'mailto:support@structure.fi',
    },
    Icon: MailButton,
  },
];

const FooterLink: VFC<
  {
    name: string;
    href: string;
  } & ComponentPropsWithRef<'li'>
> = ({ name, href, className, ref }) => {
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
    <li
      ref={ref}
      className={cn(className, 'whitespace-nowrap font-medium')}
      key={name}
      onClick={() =>
        trackUserEvent(`[Footer] Link clicked`, {
          name,
          href,
        })
      }
    >
      <Link className="font-medium text-subtitle-1 tracking-tight" href={href}>
        {name}
      </Link>
    </li>
  );
};

const Footer: VFC<ComponentPropsWithoutRef<'div'>> = ({ className }: { className?: string }) => {
  const { t } = useTranslation(['common', 'footer']);
  const { locale } = useRouter();

  const renderLinkTree = () => {
    const links = [
      [t('pages.about'), '/about'],
      [t('pages.tc'), '/legal/terms'],
      [t('pages.privacy'), '/legal/privacy'],
      [t('pages.whitepaper'), '/StructureWhitepaper.pdf'],
      [t('pages.careers'), '/careers'],
      [t('pages.blog'), '/blog'],
      [t('pages.contact'), 'mailto:support@structure.fi'],
    ];

    return (
      <div className="mt-6 flex w-full max-w-desktop flex-col mobile:mt-8 mobile:px-5 tablet:justify-between tablet:gap-10 footer:flex-row footer:items-start desktop:gap-20">
        {/* Link "Tree" Mobile */}
        <ul className="flex flex-wrap gap-y-1 tablet:hidden">
          {links.map(([name, href]) => (
            <FooterLink key={name} name={name} href={href} className="basis-1/2 pr-[35px]" />
          ))}
        </ul>

        {/* Link "Tree" Tablet/Desktop */}
        <ul className="hidden w-full min-w-0 grid-cols-1 justify-between tablet:flex tablet:max-w-[700px] desktop:gap-5 desktop:text-center">
          {links.map(([name, href]) => (
            <FooterLink key={name} name={name} href={href} />
          ))}
        </ul>
      </div>
    );
  };

  return (
    <footer
      className={cn(
        'flex w-full flex-col z-0 items-center border-t border-gray-6 px-4 pt-8 pb-10 mobile:px-6 mobile:pb-19 tablet:pt-10',
        className,
      )}
    >
      <div className="flex w-full max-w-desktop flex-col tablet:flex-row tablet:px-5">
        <div className="flex w-full flex-row gap-2">
          <Link href="https://structure-fi.onelink.me/ieej/7wjwas4h">
            <a>
              <button
                type="button"
                onClick={() => {
                  trackUserEvent(`Store Badge Clicked`, {
                    location: 'footer',
                    platform: 'ios',
                  });
                }}
              >
                <Image src="/badges/icon_badge_app-store_black.svg" width={120} height={40} />
              </button>
            </a>
          </Link>
          <Menu as="div" className="relative inline-block items-center justify-center text-left">
            <Menu.Button as="div" className="flex items-center gap-2 focus:outline-none">
              <button
                type="button"
                className="flex"
                onClick={() => {
                  trackUserEvent(`Store Badge Clicked`, {
                    location: 'footer',
                    platform: 'android',
                  });
                }}
              >
                <Image width={120} height={40} src="/badges/icon_badge_google-play_black.svg" />
              </button>
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-5 w-45 origin-top-right rounded-md bg-white text-black shadow-lg ring-1 ring-gray-6 focus:outline-none mobile:mt-4">
                <div className="py-1">
                  <Menu.Item>
                    <Link href="https://structure-fi.onelink.me/csdn/m31bmvbm">
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events */}
                      <a
                        tabIndex={0}
                        role="link"
                        onClick={() => {
                          trackUserEvent(`Android Dropdown Item Click`, {
                            location: 'footer',
                            item: 'playStore',
                          });
                        }}
                        className="block py-2 px-3 transition-colors duration-200 hover:text-blue-3"
                      >
                        Google Play
                      </a>
                    </Link>
                  </Menu.Item>
                  <Menu.Item>
                    <Link href="https://structure-fi.onelink.me/QNn4/94jz1jnd">
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events */}
                      <a
                        tabIndex={0}
                        role="link"
                        onClick={() => {
                          trackUserEvent(`Android Dropdown Item Click`, {
                            location: 'footer',
                            item: 'apkDownload',
                          });
                        }}
                        className="block py-2 px-3 transition-colors duration-200 hover:text-blue-3"
                      >
                        {t('direct_download', { ns: 'common' })}
                      </a>
                    </Link>
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>

        {/* Social Media Links */}
        <ul className="mt-6 flex shrink-0 flex-wrap gap-4 tablet:mt-0">
          {SocialMedia.map(({ name, href, Icon }) => (
            <li key={name}>
              <a href={href[locale] || href['en-US']}>
                <Icon
                  onClick={() =>
                    trackUserEvent(`[Footer] Social Icon Clicked`, {
                      name,
                      href,
                    })
                  }
                  className="h-8 w-8"
                />
              </a>
            </li>
          ))}
        </ul>
      </div>

      {renderLinkTree()}

      <div className="w-full max-w-desktop mobile:px-5">
        {/* Footnotes */}
        <ul className="mt-8 flex flex-col gap-4 text-caption tablet:mt-9">
          <div className="flex flex-row items-start gap-4">
            <span className="pl-[1px] font-light">1</span>
            <li className="pl-[2px]">
              Please see our{' '}
              <Link href="/legal/fees">
                <a className="text-blue-3">fee schedule</a>
              </Link>{' '}
              for more information.
            </li>
          </div>
          <div className="flex flex-row items-start gap-4">
            <span className="font-light">2</span>
            <li>
              Not all assets will be available to trade without restriction at any time. Assets
              currently available for trading will be displayed in the application.
            </li>
          </div>
          <div className="flex flex-row items-start gap-4">
            <span className="font-light">3</span>
            <li>
              For additional details, please review our{' '}
              <Link href="/legal/terms">
                <a className="text-blue-3">Terms of Service</a>
              </Link>
              .
            </li>
          </div>
          <div className="flex flex-row items-start gap-4">
            <span className="font-light">4</span>
            <li>
              Structure has partnered with custody providers who utilize secure multi-party
              computation and other security measures to protect your assets and information. No
              system is ever completely secure and Structure is not responsible for any losses
              incurred due to issues at our custody provider.
            </li>
          </div>
        </ul>

        {/* Disclaimer */}
        <div className="flex flex-col gap-5 pt-8 mobile:flex-row">
          <p className="flex-1 whitespace-pre-line text-caption">
            {t('disclaimer.left', { ns: 'footer' })}
          </p>
          <p className="flex-1 whitespace-pre-line text-caption">
            {t('disclaimer.right', { ns: 'footer' })}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
